/**
 * SitePlanningPanel — le planning d'un site : les réservations CONFIRMÉES à
 * venir des véhicules rattachés AUJOURD'HUI au site, par véhicule puis par jour.
 *
 * Source : GET /api/v1/sites/:id/planning?from=&to= (lot 2 « le temps », §2.3).
 * L'attache est COURANTE (`vehicle.working_site_id`), pas snapshot — c'est le
 * serveur qui le dit (`attachment: 'current'`) et c'est voulu : « quelles
 * voitures DE MON SITE sont prises demain » se lit depuis le parc d'aujourd'hui.
 *
 * Ce que le panneau tient :
 *   - une fenêtre choisie par l'utilisateur (7 / 14 / 30 jours), `to` envoyé en
 *     date civile pour que le dernier jour compte EN ENTIER ;
 *   - un état vide honnête : « aucune réservation confirmée sur cette période » ;
 *   - la troncature serveur (`coverage.truncated`) est ÉCRITE, jamais tue.
 */
import React, { useMemo, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { AlertTriangle, CalendarDays, Car } from 'lucide-react';
import { workingSiteAPI } from '../../api/admin.js';
import { useFormat } from '../../i18n/format.js';

export const WINDOW_OPTIONS = [7, 14, 30];

/** Date civile locale (AAAA-MM-JJ) à `days` jours d'aujourd'hui. */
export function civilDateInDays(days, now = new Date()) {
  const d = new Date(now.getFullYear(), now.getMonth(), now.getDate() + days);
  const mm = String(d.getMonth() + 1).padStart(2, '0');
  const dd = String(d.getDate()).padStart(2, '0');
  return `${d.getFullYear()}-${mm}-${dd}`;
}

function SitePlanningPanel({ siteId }) {
  const { t } = useTranslation();
  const { fmt } = useFormat();
  const [windowDays, setWindowDays] = useState(30);

  const to = useMemo(() => civilDateInDays(windowDays), [windowDays]);

  const { data, isLoading, isError, error, refetch } = useQuery({
    queryKey: ['sitePlanning', siteId, to],
    queryFn: () => workingSiteAPI.getPlanning(siteId, { to }),
    enabled: siteId != null,
  });

  const planning = data?.planning || [];
  const coverage = data?.coverage || {};

  return (
    <div className="space-y-4" data-testid="site-planning-panel">
      {/* Fenêtre */}
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div className="flex items-center gap-2 text-sm text-gray-600">
          <CalendarDays className="w-4 h-4 text-purple-600" />
          <span>{t('sites.planning.windowLabel')}</span>
          <div className="flex rounded-lg border border-gray-200 overflow-hidden" role="group">
            {WINDOW_OPTIONS.map(days => (
              <button
                key={days}
                type="button"
                onClick={() => setWindowDays(days)}
                aria-pressed={windowDays === days}
                className={`px-3 py-1 text-sm ${
                  windowDays === days
                    ? 'bg-purple-600 text-white'
                    : 'bg-white text-gray-700 hover:bg-gray-50'
                }`}
              >
                {t('sites.planning.windowDays', { count: days })}
              </button>
            ))}
          </div>
        </div>
        {data && (
          <p className="text-sm text-gray-500" data-testid="site-planning-summary">
            {t('sites.planning.summary', {
              vehicles: coverage.vehicles_at_site ?? 0,
              reservations: coverage.reservations_kept ?? 0,
            })}
          </p>
        )}
      </div>

      {coverage.truncated && (
        <div className="flex items-start gap-2 p-3 rounded-lg bg-amber-50 border border-amber-200 text-sm text-amber-800" data-testid="site-planning-truncated">
          <AlertTriangle className="w-4 h-4 mt-0.5 flex-shrink-0" />
          <span>{t('sites.planning.truncated', { read: coverage.reservations_read ?? 0 })}</span>
        </div>
      )}

      {isLoading && (
        <p className="text-gray-500 text-center py-8">{t('sites.planning.loading')}</p>
      )}

      {isError && (
        <div className="text-center py-8 space-y-2">
          <p className="text-red-600 text-sm">{t('sites.planning.error', { message: error?.message || '' })}</p>
          <button
            type="button"
            onClick={() => refetch()}
            className="px-3 py-1.5 text-sm bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 border border-gray-300"
          >
            {t('sites.planning.retry')}
          </button>
        </div>
      )}

      {data && planning.length === 0 && (
        <p className="text-gray-500 text-center py-8" data-testid="site-planning-empty">
          {t('sites.planning.empty')}
        </p>
      )}

      {planning.length > 0 && (
        <div className="overflow-x-auto">
          <table className="min-w-full text-sm">
            <thead>
              <tr className="text-left text-xs text-gray-500 uppercase border-b border-gray-200">
                <th className="py-2 pr-4">{t('sites.planning.columns.vehicle')}</th>
                <th className="py-2 pr-4">{t('sites.planning.columns.day')}</th>
                <th className="py-2 pr-4">{t('sites.planning.columns.slot')}</th>
                <th className="py-2 pr-4">{t('sites.planning.columns.driver')}</th>
                <th className="py-2">{t('sites.planning.columns.destination')}</th>
              </tr>
            </thead>
            <tbody>
              {planning.map(vehicle => {
                const rows = vehicle.days.flatMap(day =>
                  day.reservations.map((r, idx) => ({ day: day.day, first: idx === 0, r }))
                );
                const vehicleLabel = [vehicle.make, vehicle.model].filter(Boolean).join(' ');
                return rows.map((row, i) => (
                  <tr
                    key={row.r.reservation_id}
                    className={`border-b border-gray-100 ${i === 0 ? 'border-t border-gray-200' : ''}`}
                    data-testid={`site-planning-row-${row.r.reservation_id}`}
                  >
                    <td className="py-2 pr-4 align-top">
                      {i === 0 && (
                        <div className="flex items-center gap-2">
                          <Car className="w-4 h-4 text-blue-600 flex-shrink-0" />
                          <div>
                            <p className="font-medium text-gray-900">{vehicle.license_plate || '—'}</p>
                            {vehicleLabel && <p className="text-xs text-gray-500">{vehicleLabel}</p>}
                          </div>
                        </div>
                      )}
                    </td>
                    <td className="py-2 pr-4 align-top text-gray-700">
                      {row.first ? fmt.date(row.day) : ''}
                    </td>
                    <td className="py-2 pr-4 align-top text-gray-900 whitespace-nowrap">
                      {t('sites.planning.slot', {
                        from: row.r.planned_start ? fmt.time(row.r.planned_start) : '—',
                        to: row.r.planned_end ? fmt.time(row.r.planned_end) : '—',
                      })}
                    </td>
                    <td className="py-2 pr-4 align-top text-gray-700">
                      {row.r.driver_name || t('sites.planning.unknownDriver')}
                    </td>
                    <td className="py-2 align-top text-gray-700">
                      {row.r.destination_label || '—'}
                    </td>
                  </tr>
                ));
              })}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

export default SitePlanningPanel;
